// Thin fetch wrapper. Adds the Bearer JWT to every request; on 401/403
// clears the token (so the App's useEffect bounces back to the login
// view) and throws so callers can show a message.

import { API_BASE } from "./config";
import { clearSessionToken, getSessionToken } from "./auth";

export class ApiError extends Error {
  constructor(public statusCode: number, message: string) {
    super(message);
  }
}

export async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set("Content-Type", "application/json");
  const token = getSessionToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  const bodyText = await res.text();

  // 401 means the session itself is bad — clear and bounce.
  // 403 means the session is valid but the user lacks permission for
  // this specific resource — keep the session, just surface the error.
  if (res.status === 401) {
    clearSessionToken();
    throw new ApiError(res.status, bodyText || "unauthorized");
  }
  if (!res.ok) {
    throw new ApiError(res.status, bodyText || res.statusText);
  }
  if (!bodyText) return {} as T;
  return JSON.parse(bodyText) as T;
}

// Type aliases for the endpoints this app calls.

export interface AuthResponse {
  sessionToken: string;
  user: { id: string; email: string | null; name: string | null };
}

export type ClubKind = "club" | "highSchool" | "college";

/** One club the signed-in user may act on. */
export interface ClubMembership {
  clubId: string;
  clubName: string;
  /** Distinguishes a youth club from a school; drives label choices. */
  kind: ClubKind;
  role: "admin" | "coach" | "viewer";
  /**
   * True when the user reaches this club through the platform-level
   * super-admin flag rather than a real membership — worth surfacing so an
   * operator knows they're looking at someone else's club.
   */
  viaSuperAdmin: boolean;
  /** Populated once club logo upload ships; null until then. */
  logoUrl?: string | null;
}

export interface MeResponse {
  user: { id: string; email: string | null; name: string | null };
  /** Platform operator: `memberships` then contains every club. */
  isSuperAdmin: boolean;
  memberships: ClubMembership[];
}

/**
 * A club as the platform sees it — the super-admin Clubs page, not the
 * membership view in `/v1/me`. `slug` is nullable only for clubs that predate
 * the column; everything created through the API has one.
 */
export interface ClubRow {
  id: string;
  name: string;
  kind: ClubKind;
  slug: string | null;
  shortName: string | null;
  timezone: string;
  city: string | null;
  state: string | null;
  website: string | null;
  logoUrl: string | null;
  createdAt: string;
  memberCount: number;
}

export interface ClubsResponse {
  clubs: ClubRow[];
}

export interface CreateClubRequest {
  name: string;
  kind: ClubKind;
  /** Omit to let the server derive one from the name. */
  slug?: string;
  shortName?: string;
  timezone?: string;
  city?: string;
  state?: string;
  website?: string;
  /** Required: the club's first Club Admin, invited in the same transaction. */
  adminEmail: string;
}

/**
 * Partial update. An absent key is left alone; an explicit empty string clears
 * a nullable column. `slug` and `kind` are rejected with 403 unless the caller
 * is a super admin.
 */
export type UpdateClubRequest = Partial<{
  name: string;
  kind: ClubKind;
  slug: string;
  shortName: string;
  timezone: string;
  city: string;
  state: string;
  website: string;
}>;

export interface UpdateClubResponse {
  club: ClubRow;
}

export interface DeleteClubResponse {
  deleted: boolean;
  id: string;
  name: string;
}

export interface CreateClubResponse {
  /** The freshly created row — no `createdAt`/`memberCount` yet. */
  club: Omit<ClubRow, "createdAt" | "memberCount">;
  /**
   * Snake_case because it's the invitations row as stored. Pre-existing shape,
   * shared with POST /v1/clubs/{clubId}/invitations.
   */
  invitation: { id: string; email: string; role: string };
}

/** A club's own season schedule. Only `published` ones are ever public. */
export interface ClubSchedule {
  id: string;
  clubId: string;
  name: string;
  seasonName: string | null;
  ageGroupId: string | null;
  status: "draft" | "published";
  publishedAt: string | null;
  createdAt: string;
  /** Present on the list endpoint only. */
  eventCount?: number;
}

/**
 * One fixture. `eventDate` is `YYYY-MM-DD` and `startTime` is `HH:MM:SS` or
 * null — separate because "time TBD" is a normal state, and both are the
 * pool's local wall clock rather than UTC.
 */
export interface ScheduleEvent {
  id: string;
  scheduleId: string;
  eventDate: string;
  startTime: string | null;
  opponent: string | null;
  location: string | null;
  /**
   * Which caps the team wears, and by implication which side it starts on —
   * not where the game is played. Matches the white/dark vocabulary the app's
   * tournament schedules have always used.
   */
  capColor: "white" | "dark";
  levelLabel: string | null;
  notes: string | null;
  sortOrder: number;
}

export interface SchedulesResponse {
  schedules: ClubSchedule[];
}

export interface ScheduleDetailResponse {
  schedule: ClubSchedule;
  events: ScheduleEvent[];
}

/**
 * Bulk import. Rows match existing events on `(eventDate, opponent)` and update
 * rather than duplicate, so re-importing a corrected sheet fixes the season.
 * All-or-nothing: a bad row rejects the whole import naming its 1-based index.
 */
export interface ImportEventsResponse {
  created: number;
  updated: number;
  total: number;
}

export interface GameListItem {
  id: string;
  opponent: string | null;
  date: string;
  location: string | null;
  status: "upcoming" | "live" | "completed";
  homeScore: number;
  awayScore: number;
  uploadedAt: string;
  ageGroupCode: string | null;
  gender: string | null;
  recordedBy: { id: string; name: string | null; email: string | null } | null;
}

export interface GamesResponse {
  games: GameListItem[];
}

export interface MemberRow {
  userId: string;
  email: string | null;
  name: string | null;
  role: "admin" | "coach" | "viewer";
  joinedAt: string;
}

export interface PendingInvitation {
  id: string;
  email: string;
  role: "admin" | "coach" | "viewer";
  createdAt: string;
}

export interface MembersResponse {
  members: MemberRow[];
  invitations: PendingInvitation[];
}

export interface TeamRow {
  id: string;
  name: string;
}

export interface TeamsResponse {
  teams: TeamRow[];
}

export interface GameDetail {
  game: {
    id: string;
    opponent: string | null;
    date: string;
    location: string | null;
    status: "upcoming" | "live" | "completed";
    ageGroupCode: string | null;
    gender: string | null;
    quarterDurationMins: number;
    fieldPlayerCount: number;
    currentQuarter: number;
    /** Simple mode: tracked without subs/quarters/playing time. */
    simpleMode: boolean;
    homeScore: number;
    awayScore: number;
    uploadedAt: string;
    teamId: string;
    toggles: {
      assists: boolean;
      blocks: boolean;
      ejectionsDrawn: boolean;
      fiveM: boolean;
      shots: boolean;
      steals: boolean;
      turnovers: boolean;
    };
    recordedBy: { id: string; name: string | null; email: string | null } | null;
  };
  rosterEntries: Array<{
    id: string;
    playerId: string;
    rankOrder: number;
    capNumber: number;
    playTimeSecs: number;
    ejections: number;
    fouledOut: boolean;
    inWater: boolean;
    player: { firstName: string; lastName: string; position: string | null };
    stats: Record<string, number>;
    fiveMs: number;
  }>;
  events: Array<{
    id: string;
    rosterEntryId: string | null;
    quarter: number;
    timeInQuarter: string | null;
    type: string;
    isOpponent: boolean;
    is5M: boolean;
  }>;
  substitutions: Array<{
    id: string;
    quarter: number;
    timeInQuarter: string | null;
    outEntryId: string;
    inEntryId: string;
  }>;
  opponentStats: Record<string, number>;
}
